/**
 * Sanitises a `returnUrl` query value before a post-authentication redirect.
 *
 * Only same-origin, absolute paths are allowed (e.g. `/business/employees`).
 * Anything that could send the browser to another origin after a successful
 * login is rejected, so callers can fall back to a role-aware home route:
 *
 * - absolute URLs      → `https://evil.example`
 * - protocol-relative  → `//evil.example`
 * - backslash variant  → `/\evil.example` (browsers normalise `\` to `/`)
 * - encoded variants   → `%2F%2Fevil.example`
 *
 * @returns the safe path, or `null` when the value is missing or unsafe.
 */
export function safeReturnUrl(
    raw: string | null | undefined,
): string | null {
    if (!raw) return null;

    // `useSearchParams().get()` already decodes once, but the value can also
    // arrive still-encoded (e.g. forwarded between auth pages), so accept
    // either form — while never trusting the decoded result blindly.
    const candidates = [raw];
    try {
        const decoded = decodeURIComponent(raw);
        if (decoded !== raw) candidates.push(decoded);
    } catch {
        // Malformed percent-encoding: only the raw value is considered.
    }

    return candidates.find(isSameOriginPath) ?? null;
}

/** True when `path` is an absolute path on the current origin. */
function isSameOriginPath(path: string): boolean {
    if (!path.startsWith("/")) return false;

    // `//host` and `/\host` resolve to a different origin.
    if (path.length > 1 && (path[1] === "/" || path[1] === "\\")) return false;

    // Reject control characters (CR/LF, NUL) that can truncate or alter a URL.
    if (/[\u0000-\u001f\u007f]/.test(path)) return false;

    return true;
}
